import { useEffect, useState } from "react";
import axios from "axios";
import { Users } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * v147.93 — Public "🎟 172 of 300 arrived" attendance chip for the bio
 * page + storefront ticket product blocks.
 *
 * Silently omits itself if:
 *   • The endpoint 403s (widget not enabled for this event)
 *   • The endpoint 404s (event not found / not a ticket)
 *   • Any network error
 *
 * Polls every 30s so pressure builds during the event window without
 * hammering the public API.
 */
export default function EventAttendanceChip({ productId, testId }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!productId) return;
    let alive = true;
    const url = `${BACKEND_URL}/api/public/events/${productId}/attendance`;
    const load = async () => {
      try {
        const { data } = await axios.get(url);
        if (alive) setData(data);
      } catch {
        if (alive) setData(null);
      }
    };
    load();
    const t = setInterval(load, 30_000);
    return () => { alive = false; clearInterval(t); };
  }, [productId]);

  if (!data) return null;

  const sold = Number(data.tickets_sold || 0);
  const checkedIn = Number(data.checked_in || 0);
  const capacity = Number(data.capacity || 0);

  // Pick the most useful headline based on the event lifecycle.
  const startTs = combineDateTime(data.event_date_from, data.event_time);
  const endTs = combineDateTime(data.event_date_to || data.event_date_from, data.event_end_time);
  const now = Date.now();
  const isLive = startTs && now >= startTs && (!endTs || now <= endTs);
  const isEnded = endTs && now > endTs;

  let label, tone;
  if (isLive && checkedIn > 0) {
    // Live check-ins — most compelling social proof.
    label = capacity
      ? `${checkedIn.toLocaleString("en-IN")} of ${capacity.toLocaleString("en-IN")} arrived · Live`
      : `${checkedIn.toLocaleString("en-IN")} arrived · Live`;
    tone = "border-coral bg-coral/10 text-coral animate-pulse";
  } else if (isEnded && checkedIn > 0) {
    label = `${checkedIn.toLocaleString("en-IN")} attended`;
    tone = "border-ink/40 bg-white text-ink/70";
  } else if (sold > 0) {
    label = capacity
      ? `${sold.toLocaleString("en-IN")} of ${capacity.toLocaleString("en-IN")} sold`
      : `${sold.toLocaleString("en-IN")} tickets sold`;
    tone = "border-mint bg-mint/10 text-ink";
  } else {
    return null;
  }

  return (
    <span
      className={`inline-flex items-center gap-1 border-2 px-1.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider ${tone}`}
      data-testid={testId || `event-attendance-chip-${productId}`}
      title={isLive ? "Live check-ins at the door — updated every 30 seconds" : ""}
    >
      🎟 {label}
    </span>
  );
}

function combineDateTime(dateStr, timeStr) {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return null;
    if (timeStr) {
      const m = /^(\d{1,2}):?(\d{2})?\s*(am|pm)?$/i.exec(timeStr.trim());
      if (m) {
        let hh = parseInt(m[1], 10);
        const mm = parseInt(m[2] || "0", 10);
        const suf = (m[3] || "").toLowerCase();
        if (suf === "pm" && hh < 12) hh += 12;
        if (suf === "am" && hh === 12) hh = 0;
        d.setHours(hh, mm, 0, 0);
      }
    }
    return d.getTime();
  } catch { return null; }
}
